/**
 * TextEditor Component
 * 
 * Syncfusion DocumentEditor wrapper with:
 * - State machine-based lifecycle management
 * - Race-condition free communication
 * - Declarative configuration
 * - MutationObserver-based ribbon persistence
 * - Autosave support
 */

import { registerLicense } from '@syncfusion/ej2-base';
import {
  DocumentEditorContainerComponent,
  Ribbon,
  Toolbar,
} from '@syncfusion/ej2-react-documenteditor';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { CommentData, EditorConfig, EditorState, SavePayload } from '@/types/editor.types';
import { AutosaveIndicator } from '@components/AutoSaveIndicator/AutoSaveIndicator';
import { DocumentTypeBadge } from '@components/DocumentTypeBadge/DocumentTypeBadge';
import { VersionReasonModal } from '@components/VersionReasonModal/VersionReasonModal';
import { useAutosave } from '@hooks/useAutosave';
import { useEditorCommunication } from '@hooks/useEditorCommunication';
import { useEditorConfiguration } from '@hooks/useEditorConfiguration';
import { useEditorInit } from '@hooks/useEditorInit';
import { useEditorStateMachine } from '@hooks/useEditorStateMachine';
import { useToast } from '@hooks/useToast';
import type { EditorInitData } from '@services/editorApi.service';
import {
  autosaveTextFileVersion,
  convertHtmlToSfdt,
  fetchTextEditorInit,
  saveAsTextFileVersion,
  saveTextFileVersion,
} from '@services/editorApi.service';

import { environment } from '@config/environment.config';
import { isValidSfdt } from '@utils/security.utils';
import styles from './TextEditor.module.css';

// =============================================================================
// CONFIGURATION
// =============================================================================

// Register Syncfusion license
const SYNCFUSION_LICENSE_KEY = environment.getSyncfusionLicenseKey();
if (SYNCFUSION_LICENSE_KEY) {
  registerLicense(SYNCFUSION_LICENSE_KEY);
}

// Inject required modules
DocumentEditorContainerComponent.Inject(Toolbar, Ribbon);

// Communication config
const COMM_CONFIG = {
  // Use wildcard for parent handshake reliability across embedded hosts.
  // Parent-side origin checks in Angular still enforce iframe origin security.
  targetOrigin: '*',
  debug: false,
};

// =============================================================================
// TYPES
// =============================================================================

interface SfdtItem {
  ft?: number;
  tlp?: string;
  fieldType?: number;
  text?: string;
}

interface SfdtBlock {
  i?: SfdtItem[];
  inlines?: SfdtItem[];
}

interface SfdtComment {
  cid?: string;
  b?: SfdtBlock[];
  rc?: SfdtComment[];
  commentId?: string;
  blocks?: SfdtBlock[];
  replyComments?: SfdtComment[];
}

interface SfdtJson {
  cm?: SfdtComment[];
  comments?: SfdtComment[];
}

function normalizeSfdtComments(sfdt: SfdtJson): SfdtComment[] {
  const normalizeComment = (comment: SfdtComment): SfdtComment => ({
    cid: comment.cid ?? comment.commentId,
    b: (comment.b ?? comment.blocks ?? []).map((block) => ({
      i: (block.i ?? block.inlines ?? []).map((item) => ({
        ft: item.ft ?? item.fieldType,
        tlp: item.tlp ?? item.text,
      })),
    })),
    rc: (comment.rc ?? comment.replyComments ?? []).map(normalizeComment),
  });

  return (sfdt.cm ?? sfdt.comments ?? []).map(normalizeComment);
}

interface SfdtCommentInfo {
  commentId: string;
  author: string;
  text?: string;
  date: string;
  isResolved?: boolean;
  replyComments?: SfdtCommentReplyInfo[];
}

interface SfdtCommentReplyInfo {
  commentId: string;
  author: string;
  text?: string;
  date: string;
}

interface FileMenuClickArgs {
  item: { id: string };
}

interface CommentActionArgs {
  type: string;
  author?: string;
  cancel?: boolean;
}

interface PaneSwitchArgs {
  pane: string;
  cancel?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================
export const TextEditor: React.FC = () => {
  // Refs
  const editorRef = useRef<DocumentEditorContainerComponent>(null);
  const stateRef = useRef<EditorState>('IDLE');
  const pendingAutosaveKickoffRef = useRef(false);
  const pendingInitLoadRef = useRef(false);
  const isPreparingInitialContentRef = useRef(false);
  const pendingInitSfdtRef = useRef<string>('');
  const pendingInitDocumentNameRef = useRef<string | null>(null);
  const isOpeningFromInitRef = useRef(false);
  const isSavingAsRef = useRef(false);
  // IDs of comments+replies that existed when the document was loaded.
  // Populated synchronously from the SFDT payload — no getComments() timing issues.
  const initialCommentIdsRef = useRef<Set<string>>(new Set());
  // Add alongside initialCommentIdsRef
  const initialCommentBodiesRef = useRef<Map<string, SfdtBlock[]>>(new Map());

  // Local state for comment tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAutosaveEnabled, setIsAutosaveEnabled] = useState(true);
  const [canOverrideAutosave, setCanOverrideAutosave] = useState(true);
  const [currentUser, setCurrentUser] = useState<{ id: string; name: string } | null>(null);
  const [mentionableUsers, setMentionableUsers] = useState<Array<{ id: string; name: string; email: string }>>([]); 
  const [readOnlyMessage, setReadOnlyMessage] = useState<string | null>(null);
  const [showReadOnlyDialog, setShowReadOnlyDialog] = useState(false);
  const [isDocumentLoading, setIsDocumentLoading] = useState(true);
  // Version reason modal state
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [pendingVersionReason, setPendingVersionReason] = useState<string | null>(null);
  // Incremented on each reinit to force a clean Syncfusion component remount,
  // preventing stale ribbon groups/visibility from the previous document.
  const [editorKey, setEditorKey] = useState(0);
  // ===========================================================================
  // STATE MACHINE
  // ===========================================================================

  const stateMachine = useEditorStateMachine();

  // ===========================================================================
  // DOCUMENT HELPERS
  // ===========================================================================

  /**
   * Extract mentions from comment text
   */
  const extractMentionsFromText = useCallback((text: string): string[] => {
    if (!text || mentionableUsers.length === 0) return [];

    const mentions: string[] = [];
    const addMention = (name: string) => {
      if (!name) return;
      const user = mentionableUsers.find(
        (u) => u.name.toLowerCase() === name.toLowerCase()
      );
      if (user && !mentions.includes(user.id)) {
        mentions.push(user.id);
      }
    };

    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/html');

    // New format: <span class="e-mention-chip">Name</span>
    doc.querySelectorAll('.e-mention-chip').forEach((chip) => {
      addMention(chip.textContent?.trim() ?? '');
    });

    // Old format: <a href="mailto:...">@Name</a>
    doc.querySelectorAll('a[href^="mailto:"]').forEach((anchor) => {
      const raw = anchor.textContent?.trim() ?? '';
      addMention(raw.startsWith('@') ? raw.slice(1) : raw);
    });

    return mentions;
  }, [mentionableUsers]);

  /**
   * Extract all comments from document
   */
  const extractAllComments = useCallback((): CommentData[] => {
    const editor = editorRef.current?.documentEditor;
    if (!editor) return [];

    try {
      const commentsInfo = editor.getComments();
      
      return (commentsInfo as unknown as SfdtCommentInfo[]).map((comment) => {
        const authorUser = mentionableUsers.find((u) => u.name === comment.author);
        
        return {
          commentId: comment.commentId,
          author: comment.author,
          authorId: authorUser?.id || null,
          text: comment.text || '',
          mentions: extractMentionsFromText(comment.text || ''),
          date: comment.date,
          isResolved: comment.isResolved || false,
          replies: (comment.replyComments || []).map((reply) => {
            const replyAuthor = mentionableUsers.find((u) => u.name === reply.author);
            return {
              commentId: reply.commentId,
              author: reply.author,
              authorId: replyAuthor?.id || null,
              text: reply.text || '',
              mentions: extractMentionsFromText(reply.text || ''),
              date: reply.date,
            };
          }),
        };
      });
    } catch (error) {
      console.error('[TextEditor] Failed to extract comments:', error);
      return [];
    }
  }, [mentionableUsers, extractMentionsFromText]);

  /**
   * Extract mention user-IDs from an SFDT comment/reply body.
   * Mentions are stored as HYPERLINK fields: ft:0 → "HYPERLINK mailto:…" → ft:2 → display-text → ft:1
   */
  const extractMentionsFromSfdtBody = useCallback((blocks: SfdtBlock[]): string[] => {
    const userIds: string[] = [];
    for (const block of blocks) {
      const items = block.i ?? [];
      let inMailto = false;
      let pastSeparator = false;

      for (const item of items) {
        if (item.ft === 0) { inMailto = false; pastSeparator = false; }
        if (typeof item.tlp === 'string' && item.tlp.includes('mailto:')) {
          inMailto = true; pastSeparator = false; continue;
        }
        if (item.ft === 2 && inMailto) { pastSeparator = true; continue; }
        if (inMailto && pastSeparator && typeof item.tlp === 'string' && item.ft === undefined) {
          const name = item.tlp.trim();
          const user = mentionableUsers.find((u) => u.name.toLowerCase() === name.toLowerCase());
          if (user && !userIds.includes(user.id)) userIds.push(user.id);
        }
        if (item.ft === 1) { inMailto = false; pastSeparator = false; }
      }
    }
    return userIds;
  }, [mentionableUsers]);

  const snapshotInitialCommentsFromSfdt = useCallback((sfdtData: string) => {
    if (!sfdtData) return;

    try {
      const parsed: SfdtJson = JSON.parse(sfdtData);
      const ids = new Set<string>();
      const bodies = new Map<string, SfdtBlock[]>();

      for (const cm of normalizeSfdtComments(parsed)) {
        if (cm.cid) {
          ids.add(cm.cid);
          bodies.set(cm.cid, cm.b ?? []);
        }
        for (const rc of (cm.rc ?? [])) {
          if (rc.cid) {
            ids.add(rc.cid);
            bodies.set(rc.cid, rc.b ?? []);
          }
        }
      }

      initialCommentIdsRef.current = ids;
      initialCommentBodiesRef.current = bodies;
    } catch (_) {
      // Keep existing snapshot if parsing fails.
    }
  }, []);

  const flushPendingInitLoad = useCallback(() => {
    if (!pendingInitLoadRef.current) return;
    if (stateRef.current !== 'READY') return;

    const editor = editorRef.current?.documentEditor;
    if (!editor) return;

    const sfdtData = pendingInitSfdtRef.current;
    const documentName = pendingInitDocumentNameRef.current;

    // Don't finalize an empty load while async content preparation is still in-flight
    // (e.g. HTML -> SFDT conversion for imported large files).
    if (!sfdtData && isPreparingInitialContentRef.current) {
      return;
    }

    stateMachine.onLoadDocument();

    pendingInitLoadRef.current = false;
    pendingInitSfdtRef.current = '';
    pendingInitDocumentNameRef.current = null;

    if (documentName) {
      editor.documentName = documentName;
    }

    if (!sfdtData) {
      stateMachine.onDocumentLoaded();
      return;
    }

    snapshotInitialCommentsFromSfdt(sfdtData);
    isOpeningFromInitRef.current = true;
    editor.open(sfdtData);
  }, [snapshotInitialCommentsFromSfdt, stateMachine]);

  useEffect(() => {
    if (stateMachine.context.isDocumentLoaded) {
      setIsDocumentLoading(false);
    }
  }, [stateMachine.context.isDocumentLoaded]);

  const normalizeSfdtPayload = useCallback((input: unknown): string => {
    if (input === null || input === undefined) return '';

    // If already an object, serialize it and validate.
    // IMPORTANT: Do NOT try to unwrap {sfdt: "..."} — the compressed-SFDT format
    // uses that shape and editor.open() handles it natively. Unwrapping the
    // base64-ZIP value and re-validating it will always produce an empty result.
    if (typeof input === 'object') {
      try {
        const serialized = JSON.stringify(input);
        return isValidSfdt(serialized) ? serialized : '';
      } catch {
        return '';
      }
    }

    const text = String(input).replace(/^\uFEFF/, '').trim();
    if (!text) return '';

    // Some upload flows persist only the compressed SFDT base64 blob ("UEsDB...")
    // instead of the wrapped JSON object. Wrap it so editor.open() can consume it.
    if (/^UEsDB[A-Za-z0-9+/=]+$/.test(text)) {
      return JSON.stringify({ sfdt: text });
    }

    // Already a valid SFDT string (full or compressed format)?
    if (isValidSfdt(text)) return text;

    // May be a double-serialized string: JSON.parse to unwrap one level
    try {
      const parsed = JSON.parse(text) as unknown;

      if (typeof parsed === 'string') {
        // One level of extra serialization — unwrap and validate
        const nested = parsed.replace(/^\uFEFF/, '').trim();
        if (/^UEsDB[A-Za-z0-9+/=]+$/.test(nested)) {
          return JSON.stringify({ sfdt: nested });
        }
        if (isValidSfdt(nested)) return nested;
        // Two levels deep: unwrap the inner string once more
        try {
          const nestedParsed = JSON.parse(nested) as unknown;
          if (typeof nestedParsed === 'object' && nestedParsed !== null) {
            const nestedSerialized = JSON.stringify(nestedParsed);
            return isValidSfdt(nestedSerialized) ? nestedSerialized : '';
          }
        } catch {
          return '';
        }
        return '';
      }

      // Parsed to an object — serialize and validate (handles all object shapes)
      if (typeof parsed === 'object' && parsed !== null) {
        const serialized = JSON.stringify(parsed);
        return isValidSfdt(serialized) ? serialized : '';
      }
    } catch {
      return '';
    }

    return '';
  }, []);

  /**
   * Build save payload.
   * Uses SFDT cm[].cid for both the initial snapshot and the current state
   * so comment-ID matching is always reliable (getComments() doesn't expose IDs).
   */
  const buildSavePayload = useCallback((): SavePayload => {
    const container = editorRef.current;
    const sfdt = container?.documentEditor.serialize() || '';
    const documentName = container?.documentEditor.documentName || 'Untitled';
    const allComments = extractAllComments();

    // Each entry = the set of user IDs tagged in one new/edited comment or reply.
    // Using one entry per comment (not a global Set) means a user tagged in N
    // different comments will receive N separate notifications.
    const sessionMentions: Array<{ users: string[] }> = [];

    const addMentionGroup = (userIds: string[]) => {
      if (userIds.length > 0) {
        sessionMentions.push({ users: userIds });
      }
    };

    try {
      const sfdtJson: SfdtJson = JSON.parse(sfdt);
      const sfdtComments = normalizeSfdtComments(sfdtJson);

      // Advance snapshot so subsequent saves don't re-notify
      const next = new Map<string, SfdtBlock[]>();

      for (const cm of sfdtComments) {
        if (!cm.cid) continue;

        if (!initialCommentIdsRef.current.has(cm.cid)) {
          // Genuinely new comment — capture all mentions for this comment
          addMentionGroup(extractMentionsFromSfdtBody(cm.b ?? []));
        } else {
          // Existing comment — only capture newly added mentions if body was edited
          const prevBody = initialCommentBodiesRef.current.get(cm.cid);
          if (hasBodyChanged(prevBody ?? [], cm.b ?? [])) {
            addMentionGroup(getAddedMentions(prevBody ?? [], cm.b ?? [], extractMentionsFromSfdtBody));
          }
        }
        next.set(cm.cid, cm.b ?? []);  // advance snapshot

        for (const rc of (cm.rc ?? [])) {
          if (!rc.cid) continue;

          if (!initialCommentIdsRef.current.has(rc.cid)) {
            addMentionGroup(extractMentionsFromSfdtBody(rc.b ?? []));
          } else {
            const prevBody = initialCommentBodiesRef.current.get(rc.cid);
            if (hasBodyChanged(prevBody ?? [], rc.b ?? [])) {
              addMentionGroup(getAddedMentions(prevBody ?? [], rc.b ?? [], extractMentionsFromSfdtBody));
            }
          }
          next.set(rc.cid, rc.b ?? []);  // advance snapshot
        }
      }

      // Advance both refs so subsequent saves don't re-notify
      initialCommentIdsRef.current = new Set(next.keys());
      initialCommentBodiesRef.current = next;
    } catch (err) {
      console.error('[TextEditor] SFDT parse failed:', err);
    }
    return {
      content: { sfdt },
      comments: {
        allComments,
        sessionMentions,
      },
      metadata: {
        documentName,
        timestamp: Date.now(),
        componentType: 'textEditor',
      },
    };
  }, [extractAllComments, extractMentionsFromSfdtBody]);

  // ===========================================================================
  // AUTOSAVE
  // ===========================================================================

  const autosave = useAutosave({
    // Spread autosave config but override enabled to match toggle state
    config: stateMachine.config?.autosave
      ? { ...stateMachine.config.autosave, enabled: isAutosaveEnabled }
      : undefined,
    isDirty: hasUnsavedChanges,
    canSave: !!(
      isAutosaveEnabled &&
      stateMachine.config?.permissions.save &&
      stateMachine.config?.canEdit &&
      stateMachine.state === 'ACTIVE'
    ),
    onSave: async () => {
      const id = versionIdRef.current;
      if (!id) return;
      try {
        // Serialize SFDT directly — do NOT call buildSavePayload() here.
        // buildSavePayload() advances the comment snapshot as a side-effect,
        // which would cause a subsequent manual save to miss new comment mentions
        // (the snapshot would already look "up to date" so no sessionMentions
        // would be collected and no notifications would fire).
        const sfdtData = editorRef.current?.documentEditor.serialize() || '';
        await autosaveTextFileVersion({ _id: id, sfdtData, isAutosaveEnabled });
        autosave.onSaveComplete();
        setHasUnsavedChanges(false);
      } catch (err) {
        console.error('[TextEditor] Autosave failed:', err);
        autosave.onSaveFailed((err as Error).message || 'Autosave failed');
      }
    },
  });
  function hasBodyChanged(prev: SfdtBlock[], next: SfdtBlock[]): boolean {
    return JSON.stringify(prev) !== JSON.stringify(next);
  }
  function getAddedMentions(
    prevBody: SfdtBlock[],
    nextBody: SfdtBlock[],
    extractor: (blocks: SfdtBlock[]) => string[]
  ): string[] {
    const prevMentions = new Set(extractor(prevBody));
    return extractor(nextBody).filter((id) => !prevMentions.has(id));
  }

  // ===========================================================================
  // COMMUNICATION
  // ===========================================================================

  const communication = useEditorCommunication(COMM_CONFIG);

  // ===========================================================================
  // TOAST NOTIFICATIONS
  // ===========================================================================

  const { showWarning } = useToast();

  // Keep latest machine state in a ref for async timers in init flow.
  useEffect(() => {
    stateRef.current = stateMachine.state;
  }, [stateMachine.state]);

  // If first change arrived while transitioning READY -> ACTIVE,
  // start autosave timers once ACTIVE is committed.
  useEffect(() => {
    if (stateMachine.state === 'ACTIVE' && pendingAutosaveKickoffRef.current) {
      pendingAutosaveKickoffRef.current = false;
      autosave.onContentChange();
    }
  }, [stateMachine.state, autosave]);

  // ===========================================================================
  // API-DRIVEN INITIALIZATION (shared hook handles init + reinit)
  // ===========================================================================

  const buildTextEditorConfig = useCallback((data: EditorInitData): EditorConfig => {
    const canEdit = data.permissions.save && !data.lockInfo.isLocked;
    const canComment = data.permissions.save && data.version.allowComment === true;
    const defaultAutosaveEnabled = data.autosavePolicy?.defaultEnabled ?? true;
    const allowUserOverride = data.autosavePolicy?.allowUserOverride ?? true;
    const isTemplatePolicy = data.autosavePolicy?.isTemplatePolicy ?? false;
    const templateDefaultEnabled = data.autosavePolicy?.templateDefaultEnabled ?? true;

    return {
      componentType: 'textEditor',
      documentType: data.documentType,
      canEdit,
      canComment,
      permissions: {
        save: data.permissions.save && (!data.lockInfo.isLocked || canComment),
        saveAs: data.permissions.saveAs,
        export: data.permissions.export,
        print: data.permissions.print,
      },
      comments: {
        currentUser:
          data.currentUser?._id && data.currentUser?.name
            ? { id: data.currentUser._id, name: data.currentUser.name, email: data.currentUser.email || '' }
            : data.version.owner
              ? { id: data.version.owner._id, name: data.version.owner.name, email: data.version.owner.email }
              : { id: '', name: '', email: '' },
        mentionableUsers: (data.users || [])
          .map((eu) => eu?.user)
          .filter((u): u is import('@services/editorApi.service').User => !!u?._id && !!u?.name)
          .map((u) => ({ id: u._id, name: u.name!, email: u.email || '' })),
      },
      metadata: {
        name: data.version.fullName || data.version.name,
        version: `v${data.version.versionNumber}`,
        id: data.version._id,
      },
      autosave: canEdit
        ? { enabled: defaultAutosaveEnabled, debounceMs: 3000, maxIntervalMs: 30000 }
        : undefined,
      allowVersionCreationDisabled: data.allowVersionCreationDisabled,
      autosavePolicy: {
        defaultEnabled: defaultAutosaveEnabled,
        allowUserOverride,
        isTemplatePolicy,
        templateDefaultEnabled,
      },
    };
  }, []);

  const { versionIdRef, initDataRef } = useEditorInit({
    communication,
    stateMachine,
    autosave,
    fetchInitData: (entityId, initPayload) => fetchTextEditorInit(entityId, initPayload.historyEntryId),
    buildConfig: buildTextEditorConfig,
    callbacks: {
      onBeforeReinit: () => {
        // Reset comment tracking for the new version
        initialCommentIdsRef.current = new Set();
        initialCommentBodiesRef.current = new Map();
        setHasUnsavedChanges(false);
        setIsDocumentLoading(true);
        pendingAutosaveKickoffRef.current = false;
      },
      onInitData: (data, _isReinit) => {
        // Determine read-only message based on permissions / lock state
        if (!data.permissions.save) {
          setReadOnlyMessage('This document is read-only. You do not have permission to edit it.');
        } else if (data.version.allowComment === true && data.lockInfo.isLocked) {
          setReadOnlyMessage('Content editing is disabled during lifecycle status transition. You can add comments.');
        } else if (data.lockInfo.isLocked) {
          const msg = data.lockInfo.lockReason || 'This document is currently locked and cannot be edited.';
          setReadOnlyMessage(msg);
        } else {
          setReadOnlyMessage(null);
        }

        // Set mentionable users from entity users
        setMentionableUsers(
          (data.users || [])
            .map(u => u?.user)
            .filter((u): u is import('@services/editorApi.service').User => !!u?._id && !!u?.name)
            .map((u) => ({ id: u._id, name: u.name!, email: u.email || '' }))
        );

        // Set current user from dedicated /api/users/me response, fallback to owner
        if (data.currentUser?._id && data.currentUser?.name) {
          setCurrentUser({ id: data.currentUser._id, name: data.currentUser.name });
        } else if (data.version.owner?._id && data.version.owner?.name) {
          setCurrentUser({ id: data.version.owner._id, name: data.version.owner.name });
        }

        // Apply autosave defaults from custom text template policy (if present).
        const policy = data.autosavePolicy;
        setIsAutosaveEnabled(policy?.defaultEnabled ?? true);
        setCanOverrideAutosave(policy?.allowUserOverride ?? true);
      },
      onConfigDispatched: (editorConfig, isReinit) => {
        // On reinit, force-remount the Syncfusion editor so the ribbon starts
        // from a clean slate — removing stale custom groups or visibility
        // state carried over from the previous document type.
        if (isReinit) {
          setEditorKey(k => k + 1);
        }
        // Queue document load and flush when editor+state become READY.
        pendingInitLoadRef.current = true;
        setIsDocumentLoading(true);
        pendingInitDocumentNameRef.current = editorConfig.metadata?.name || null;

        const prepareInitialContent = async () => {
          isPreparingInitialContentRef.current = true;
          pendingInitSfdtRef.current = '';

          try {
            const version = initDataRef.current?.version;
            const sfdtData = version?.sfdtData || '';
            const htmlOrSerializedData = version?.data || '';
            const normalizedSfdt = normalizeSfdtPayload(sfdtData);
            const normalizedDataSfdt = normalizeSfdtPayload(htmlOrSerializedData);

            if (normalizedSfdt) {
              pendingInitSfdtRef.current = normalizedSfdt;
              return;
            }

            if (normalizedDataSfdt) {
              pendingInitSfdtRef.current = normalizedDataSfdt;
              return;
            }

            if (!htmlOrSerializedData) {
              pendingInitSfdtRef.current = '';
              return;
            }

            try {
              const converted = await convertHtmlToSfdt(htmlOrSerializedData);
              pendingInitSfdtRef.current = normalizeSfdtPayload(converted);
            } catch (error) {
              console.error('[TextEditor] Failed to convert HTML content to SFDT:', error);
              pendingInitSfdtRef.current = '';
            }
          } finally {
            isPreparingInitialContentRef.current = false;
            flushPendingInitLoad();
          }
        };

        void prepareInitialContent();
      },
    },
  });

  useEffect(() => {
    flushPendingInitLoad();
  }, [stateMachine.state, flushPendingInitLoad]);

  // Show read-only dialog when user mousedowns inside the document content area.
  // We attach to the content viewport (.e-de-viewercontainer) rather than the
  // whole editor element, so the ribbon, toolbar, and file menu are excluded
  // by DOM structure — not fragile CSS-class guards.
  useEffect(() => {
    if (stateMachine.state !== 'ACTIVE') return;
    if (stateMachine.config?.canComment) {
      setShowReadOnlyDialog(false);
      return;
    }

    const editor = editorRef.current?.documentEditor;
    if (!editor?.isReadOnly) return;

    // The document content scroll container — everything below the ribbon.
    const contentArea =
      (editorRef.current?.element?.querySelector('.e-de-viewercontainer') ??
       editorRef.current?.element?.querySelector('.e-documenteditor') ??
       editorRef.current?.element?.querySelector('.e-de-ctn-body')) as HTMLElement | null;

    if (!contentArea) return;

    const handleMousedown = () => setShowReadOnlyDialog(true);
    contentArea.addEventListener('mousedown', handleMousedown);
    return () => contentArea.removeEventListener('mousedown', handleMousedown);
  }, [stateMachine.state, stateMachine.config?.canComment]);

  // ===========================================================================
  // CONTROL COPY PROTECTION
  // Fires whenever the state machine enters APPLYING_PROTECTION.
  // The state machine reaches this state when documentType === 'control' and
  // canEdit === true — meaning the user has write permission but the document
  // must be restricted to pre-marked editable regions only.
  // ===========================================================================

  useEffect(() => {
    console.log(stateMachine.state, 'Applying protection');
    if (stateMachine.state !== 'APPLYING_PROTECTION') return;
    const editor = editorRef.current?.documentEditor;
    const editorModule = editor?.editor as unknown as {
      enforceProtection?: (pwd: string, type: string) => void;
      enforceDocumentProtection?: (pwd: string, type: string) => void;
    };

    if (!editor || !editorModule) {
      stateMachine.onProtectionApplied();
      return;
    }

    try {
      if (currentUser?.name) {
        (editor as unknown as { currentUser?: string }).currentUser = currentUser.name;
      }

      editor.documentEditorSettings.highlightEditableRanges = true;

      if (typeof editorModule.enforceProtection === 'function') {
        editorModule.enforceProtection('', 'ReadOnly');
      } else if (typeof editorModule.enforceDocumentProtection === 'function') {
        editorModule.enforceDocumentProtection('', 'ReadOnly');
      }

      try {
        editor.showRestrictEditingPane(false);
      } catch (_) {}
    } catch (err) {
      console.warn('[TextEditor] Control copy protection enforcement failed:', err);
    }

    stateMachine.onProtectionApplied();
  }, [stateMachine.state, currentUser?.name]);

  useEffect(() => {
    if (stateMachine.state !== 'ACTIVE') return;

    if (stateMachine.config?.canEdit) {
      const editor = editorRef.current?.documentEditor;
      const editorModule = editor?.editor as unknown as {
        stopProtection?: (pwd?: string) => void;
      };

      try {
        editorModule?.stopProtection?.('');
        if (editor) editor.isReadOnly = false;
      } catch (err) {
        console.warn('[TextEditor] Failed to clear comment-only protection:', err);
      }
      return;
    }

    if (!stateMachine.config?.canComment) return;

    const editor = editorRef.current?.documentEditor;
    const editorModule = editor?.editor as unknown as {
      enforceProtection?: (pwd: string, type: string) => void;
      enforceDocumentProtection?: (pwd: string, type: string) => void;
    };

    if (!editor || !editorModule) return;

    try {
      editor.isReadOnly = false;
      if (typeof editorModule.enforceProtection === 'function') {
        editorModule.enforceProtection('', 'CommentsOnly');
      } else if (typeof editorModule.enforceDocumentProtection === 'function') {
        editorModule.enforceDocumentProtection('', 'CommentsOnly');
      }

      try {
        editor.showRestrictEditingPane(false);
      } catch (_) {}
    } catch (err) {
      console.warn('[TextEditor] Comment-only protection enforcement failed:', err);
    }
  }, [stateMachine.state, stateMachine.config?.canComment, stateMachine.config?.canEdit]);

  // ===========================================================================
  // CONFIGURATION HOOK
  // ===========================================================================

  const configuration = useEditorConfiguration({
    editorRef,
    config: stateMachine.config,
    onConfigApplied: () => {
      console.log('[TextEditor] Configuration applied');
      stateMachine.onConfigApplied();
    },
    onAddEditableRegion: () => {
      console.log('[TextEditor] Editable region added');
      setHasUnsavedChanges(true);
    },
    onClearEditableRegions: () => {
      console.log('[TextEditor] Editable regions cleared');
      setHasUnsavedChanges(true);
    },
  });

  // ===========================================================================
  // FILE MENU HANDLER
  // ===========================================================================

  const handleFileMenuClick = useCallback((args: FileMenuClickArgs) => {
    const { id } = args.item;

    if (id === 'save') {
      // Check if save is allowed
      if (!stateMachine.config?.permissions.save || (!stateMachine.config?.canEdit && !stateMachine.config?.canComment)) {
        console.warn('[TextEditor] Save not permitted');
        return;
      }

      // Pause autosave during manual save
      autosave.onManualSaveStart();

      const id = versionIdRef.current;
      if (!id) return;
      const payload = buildSavePayload();
      saveTextFileVersion({
        _id: id,
        sfdtData: payload.content.sfdt || '',
        data: payload.content.html,
        sessionMentions: payload.comments.sessionMentions,
        commentsOnly: !stateMachine.config?.canEdit && stateMachine.config?.canComment === true,
        isAutosaveEnabled,
      })
        .then(() => {
          autosave.onSaveComplete();
          setHasUnsavedChanges(false);
          console.log('[TextEditor] Save completed');
        })
        .catch((err) => {
          console.error('[TextEditor] Save failed:', err);
          autosave.onSaveFailed((err as Error).message || 'Save failed');
        });
    } else if (id === 'save_as') {
      // Save As is always available if permitted (even in readonly)
      if (!stateMachine.config?.permissions.saveAs) {
        console.warn('[TextEditor] Save As not permitted');
        return;
      }

      // Check if version creation is disabled by lifecycle template
      const allowVersionCreationDisabled = initDataRef.current?.allowVersionCreationDisabled ?? false;

      if (allowVersionCreationDisabled) {
        console.warn('[TextEditor] Save As disabled: lifecycle template does not allow version creation in current state');
        showWarning('Version cannot be created because the current status does not allow version creation.');
        return;
      }

      // Check if entity is under state transition (approval pending)
      const isUnderTransition = initDataRef.current?.version?.lifecycleProperties?.isUnderTransition ?? false;
      console.log('[TextEditor] Save As - isUnderTransition check:', {
        isUnderTransition,
        lifecycleProperties: initDataRef.current?.version?.lifecycleProperties,
      });

      if (isUnderTransition) {
        console.warn('[TextEditor] Save As disabled: entity is under status transition');
        showWarning('Cannot create new version while entity is under status transition. Please wait for the current transition to complete.');
        return;
      }

      // Guard against duplicate concurrent save-as calls
      if (isSavingAsRef.current) {
        console.warn('[TextEditor] Save As already in progress, ignoring duplicate request');
        return;
      }

      // Show the reason modal instead of directly calling API
      setIsReasonModalOpen(true);
    }

    // Also call configuration handler for any additional processing
    configuration.handleFileMenuClick(args);
  }, [stateMachine.config, buildSavePayload, showWarning, configuration, autosave]);

  // ===========================================================================
  // VERSION REASON MODAL HANDLERS
  // ===========================================================================

  const handleReasonModalConfirm = useCallback(async (reason: string) => {
    setIsReasonModalOpen(false);
    isSavingAsRef.current = true;

    // Pause autosave during save as
    autosave.onManualSaveStart();

    try {
      const id = versionIdRef.current;
      if (!id) {
        isSavingAsRef.current = false;
        return;
      }

      const payload = buildSavePayload();
      // New version resets to the template default (or true for non-template files)
      const newVersionDefault = stateMachine.config?.autosavePolicy?.templateDefaultEnabled ?? true;
      const newVersion = await saveAsTextFileVersion({
        fromVersionId: id,
        sfdtData: payload.content.sfdt || '',
        data: payload.content.html,
        versionReason: reason,
        sessionMentions: payload.comments.sessionMentions,
        prevIsAutosaveEnabled: isAutosaveEnabled,
        newVersionAutosaveDefault: newVersionDefault,
      });

      versionIdRef.current = newVersion._id;
      autosave.onSaveAsComplete();
      setHasUnsavedChanges(false);
      setIsAutosaveEnabled(newVersionDefault);
      console.log('[TextEditor] Save As completed, new version:', newVersion._id);
      // Navigate to the new version in Angular
      communication.sendNavigate('TEXTFILE_VERSION', newVersion._id, newVersion.fullName);
    } catch (err) {
      console.error('[TextEditor] Save As failed:', err);
      autosave.onSaveFailed((err as Error).message || 'Save As failed');
    } finally {
      isSavingAsRef.current = false;
    }
  }, [buildSavePayload, communication, autosave]);

  const handleReasonModalCancel = useCallback(() => {
    setIsReasonModalOpen(false);
    setPendingVersionReason(null);
  }, []);

  // ===========================================================================
  // SYNCFUSION EVENT HANDLERS
  // ===========================================================================

  const handleEditorCreated = useCallback(() => {
    console.log('[TextEditor] Syncfusion editor created');
    
    // Transition state machine
    stateMachine.onEditorCreated();
    
    // Apply configuration (handled by useEditorConfiguration hook)
    configuration.handleEditorCreated();

    // If init arrived before editor creation, flush queued load now.
    flushPendingInitLoad();
  }, [stateMachine, configuration, flushPendingInitLoad]);

  const handleDocumentChange = useCallback(() => {
    if (!isOpeningFromInitRef.current) return;

    isOpeningFromInitRef.current = false;
    stateMachine.onDocumentLoaded();
  }, [stateMachine]);

  const handleContentChange = useCallback(() => {
    // Opening a document can emit content changes; treat first one as load-complete.
    if (isOpeningFromInitRef.current) {
      isOpeningFromInitRef.current = false;
      stateMachine.onDocumentLoaded();
      return;
    }

    const shouldActivateEditor =
      stateMachine.state === 'READY' && stateMachine.context.isDocumentLoaded;

    if (shouldActivateEditor) {
      pendingAutosaveKickoffRef.current = true;
      stateMachine.onActivateEditor();
    }

    if (!hasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
    
    // Notify autosave of content change
    if (stateMachine.state === 'ACTIVE') {
      autosave.onContentChange();
    }
  }, [
    hasUnsavedChanges,
    autosave,
    stateMachine,
    stateMachine.state,
    stateMachine.context.isDocumentLoaded,
  ]);

  const handleBeforeCommentAction = useCallback((args: CommentActionArgs) => {
    // Track mentions for new comments/replies — runs regardless of currentUser
    // Prevent deletion of others' comments — only when currentUser is known
    if (args.type === 'Delete' && currentUser) {
      const commentAuthor = args.author;
      if (commentAuthor !== currentUser.name) {
        args.cancel = true;
        console.log('[TextEditor] Comment deletion blocked - not author');
      }
    }
  }, [currentUser]);

  const handleBeforePaneSwitch = useCallback((args: PaneSwitchArgs) => {
    // Prevent restrict editing pane from showing
    if (args.pane === 'RestrictEditingPane') {
      args.cancel = true;
    }
  }, []);

  // ===========================================================================
  // DOCUMENT EDITOR SETTINGS
  // ===========================================================================

  const documentEditorSettings = useMemo(() => ({
    showRuler: true,
    optimizeSfdt: false,
    mentionSettings: mentionableUsers.length > 0 ? {
      dataSource: mentionableUsers.map((user) => ({
        id: user.id,
        name: user.name,
        email: user.email,
      })),
      fields: { text: 'name' },
      popupWidth: '250px',
      popupHeight: '200px',
      allowSpaces: true,
    } : undefined,
  }), [mentionableUsers]);

  const showLoadingOverlay = useMemo(() => {
    if (!stateMachine.config) return true;

    // Keep loader visible only while document is not yet confirmed loaded
    // or while explicit load/protection phases are active.
    if (!stateMachine.context.isDocumentLoaded) return true;
    if (isDocumentLoading) return true;

    return ['LOADING_DOCUMENT', 'APPLYING_PROTECTION'].includes(stateMachine.state);
  }, [isDocumentLoading, stateMachine.config, stateMachine.context.isDocumentLoaded, stateMachine.state]);

  const loadingMessage = useMemo(() => {
    if (!stateMachine.config) return 'Preparing editor...';

    if (!stateMachine.context.isDocumentLoaded) return 'Opening document...';
    if (stateMachine.state === 'LOADING_DOCUMENT') return 'Loading document content...';
    if (stateMachine.state === 'APPLYING_PROTECTION') return 'Applying document protections...';
    if (isDocumentLoading) return 'Opening document...';

    return 'Preparing editor...';
  }, [isDocumentLoading, stateMachine.config, stateMachine.context.isDocumentLoaded, stateMachine.state]);

  const editorStatusBadges = useMemo(() => {
    const badges: Array<{ label: string; type: 'readonly' | 'state'; color?: string | null }> = [];
    const lifecycleProperties = initDataRef.current?.version?.lifecycleProperties;
    const currentState = lifecycleProperties?.currentState;

    if (stateMachine.config && !stateMachine.config.canEdit) {
      badges.push({ label: 'Readonly', type: 'readonly' });
    }

    if (currentState) {
      badges.push({ label: currentState, type: 'state', color: lifecycleProperties?.color });
    }

    return badges;
  }, [stateMachine.config]);

  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (
    <div className={styles.container}>
      {stateMachine.state === 'ACTIVE' && stateMachine.config?.canComment && (
        <div className={styles.transitionBanner} role="status" aria-live="polite">
          <span aria-hidden="true">🔒</span>
          <span>Content editing is disabled during lifecycle status transition. You can add comments.</span>
        </div>
      )}

      {/* Loading Overlay - keeps UX responsive while large SFDT loads */}
      {showLoadingOverlay && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <div className={styles.spinner} />
            <p>{loadingMessage}</p>
          </div>
        </div>
      )}

      {/* Read-only dialog — shown when user clicks inside the document content area */}
      {showReadOnlyDialog && readOnlyMessage && (
        <div className={styles.readOnlyDialogBackdrop} onClick={() => setShowReadOnlyDialog(false)}>
          <div className={styles.readOnlyDialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.readOnlyDialogContent}>
              {readOnlyMessage}
            </div>
            <div className={styles.readOnlyDialogFooter}>
              <button className={styles.readOnlyDialogOk} onClick={() => setShowReadOnlyDialog(false)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Document Editor - only render when config is available */}
      {stateMachine.config && (
        <div className={styles.editorContainer}>
          {/* Keep editor badges aligned with the ribbon, below the transition banner. */}
          <div className={styles.toolbarOverlay}>
            <DocumentTypeBadge documentType={stateMachine.config?.documentType ?? null} />
            {editorStatusBadges.length > 0 && (
              <div className={styles.statusBadges}>
                {editorStatusBadges.map((badge) => (
                  <span
                    key={`${badge.type}-${badge.label}`}
                    className={`${styles.statusBadge} ${badge.type === 'readonly' ? styles.readonlyBadge : styles.stateBadge}`}
                    style={badge.type === 'state' ? { backgroundColor: badge.color || '#007bff', color: 'black' } : undefined}
                  >
                    {badge.label}
                  </span>
                ))}
              </div>
            )}
            <AutosaveIndicator
              status={autosave.state.status}
              lastSavedAt={autosave.state.lastSavedAt}
              error={autosave.state.error}
            />
            {stateMachine.config?.canEdit && (
              <div
                title={
                  !canOverrideAutosave
                    ? (isAutosaveEnabled ? 'Autosave is enforced by template' : 'Autosave is disabled by template')
                    : (isAutosaveEnabled ? 'Autosave enabled (click to disable)' : 'Autosave disabled (click to enable)')
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginRight: '8px',
                  cursor: canOverrideAutosave ? 'pointer' : 'not-allowed',
                  opacity: canOverrideAutosave ? 1 : 0.7,
                }}
                onClick={() => {
                  if (!canOverrideAutosave) return;
                  const newToggleValue = !isAutosaveEnabled;
                  setIsAutosaveEnabled(newToggleValue);

                  if (stateMachine.config?.permissions.save && stateMachine.config?.canEdit) {
                    autosave.onManualSaveStart();
                    const id = versionIdRef.current;
                    if (id) {
                      const payload = buildSavePayload();
                      saveTextFileVersion({
                        _id: id,
                        sfdtData: payload.content.sfdt || '',
                        data: payload.content.html,
                        sessionMentions: payload.comments.sessionMentions,
                        isAutosaveEnabled: newToggleValue,
                      })
                        .then(() => {
                          autosave.onSaveComplete();
                        })
                        .catch((err) => {
                          console.error('[TextEditor] Failed to save toggle state:', err);
                          autosave.onSaveFailed((err as Error).message || 'Save failed');
                        });
                    }
                  }
                }}
              >
                <span style={{ fontSize: '13px', fontFamily: 'Mulish, system-ui, -apple-system, sans-serif', fontWeight: 400, color: 'inherit' }}>
                  Autosave
                </span>
                <div
                  style={{
                    position: 'relative',
                    width: '36px',
                    height: '20px',
                    borderRadius: '10px',
                    backgroundColor: isAutosaveEnabled ? '#1976d2' : '#ccc',
                    transition: 'background-color 0.2s',
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '2px',
                      left: isAutosaveEnabled ? '18px' : '2px',
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      transition: 'left 0.2s',
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <DocumentEditorContainerComponent
            key={editorKey}
            ref={editorRef}
            id="document-editor"
            height="100%"
            enableToolbar={true}
            toolbarMode="Ribbon"
            serviceUrl={environment.getSyncfusionServiceUrl()}
            created={handleEditorCreated}
            documentChange={handleDocumentChange}
            contentChange={handleContentChange}
            beforeCommentAction={handleBeforeCommentAction}
            beforePaneSwitch={handleBeforePaneSwitch}
            documentEditorSettings={documentEditorSettings}
            currentUser={currentUser?.name}
            fileMenuItems={configuration.fileMenuItems}
            fileMenuItemClick={handleFileMenuClick}
          />
        </div>
      )}

      {/* Version Reason Modal */}
      <VersionReasonModal
        isOpen={isReasonModalOpen}
        title="Create New Version"
        placeholder="Enter reason for creating a new version..."
        confirmBtnText="Save Version"
        cancelBtnText="Cancel"
        onConfirm={handleReasonModalConfirm}
        onCancel={handleReasonModalCancel}
      />
    </div>
  );
};


export default TextEditor;